import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash';
import { Observable, of, race, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { CustomerMultipleNewUserComponent } from '../../theme/components/customer/customer-multiple-new-user/customer-multiple-new-user.component';
import { CustomerService, ReservationService } from '../../theme/pages/home/_services';
import { AppModulesType } from '../enums/appModulesType.enum';
import { CusomerCreatedFromType } from '../enums/customer/cusomerCreatedFromType.enum';
import { Customer, EventUpdate } from '../model';
import { Reservation } from '../model/reservation.model';
import { Event } from './../../_shared/model/event.model';
import { ActiveModulesService } from './activeModules.service';
import { HelperNavigateService } from './helper-navigate.service';
import { MfToastService } from './mfToast.service';

@Injectable({
    providedIn: 'root',
})
export class SimilarCustomersService {
    constructor(
        private customerRest: CustomerService,
        private toast: MfToastService,
        private reservationRest: ReservationService,
        private navigationHelper: HelperNavigateService,
        private modalService: NgbModal,
        private activeModules: ActiveModulesService,
    ) {}

    public findSimilarCustomers(contractorId: number, resRequestData: Event | Reservation, canNavigateToReservation = false): Observable<Customer> {
        return this.customerRest.findSimilarCustomersCustomer(contractorId, resRequestData?.customer?.id).pipe(
            switchMap((customers: Customer[]) => this.manageSimilarCustomers(contractorId, resRequestData?.customer, customers, resRequestData)),
            tap(res => {
                if (res && canNavigateToReservation) {
                    this.navigationHelper.navigateToReservaton(
                        resRequestData?.prereservationId,
                        this.activeModules.isAM(AppModulesType.HELLO_DOCTOR),
                        {
                            telemed: resRequestData?.telemed,
                            phoneCall: resRequestData?.phoneCall,
                        },
                    );
                }
            }),
            catchError(err => {
                this.toast.error('Napaka pri urejanju stranke');
                return throwError(err);
            }),
        );
    }

    private manageSimilarCustomers(
        contractorId: number,
        customerNew: Customer,
        customerOld: Customer[],
        resRequestData: Event | Reservation,
    ): Observable<Customer> {
        return of(null).pipe(
            switchMap(() => {
                if (!Array.isArray(customerOld) || customerOld.length === 0) {
                    if (customerNew.createdFrom === CusomerCreatedFromType.PATIENT_APP) {
                        return this.createCustomerFromExternalEvent(contractorId, customerNew).pipe(map(() => customerNew));
                    }
                    return of(customerNew);
                } else {
                    return of(null).pipe(
                        switchMap(() => {
                            this.toast.info('V sistemu je vnešena stranka s podobnimi podatki.');
                            const modalMultipleCustomers = this.modalService.open(CustomerMultipleNewUserComponent, {
                                backdropClass: 'mf-second-modal-backdrop',
                                size: 'lg',
                                windowClass: 'mf-transparent-popup',
                            });

                            modalMultipleCustomers.componentInstance.customerNew = customerNew;
                            modalMultipleCustomers.componentInstance.customersOld = customerOld;
                            modalMultipleCustomers.componentInstance.existingCustomer = true;

                            return race(modalMultipleCustomers.closed, modalMultipleCustomers.dismissed).pipe(
                                switchMap(resultCustomerFromDb => {
                                    if (typeof resultCustomerFromDb === 'string' && (resultCustomerFromDb as string)?.includes('Close')) {
                                        return of(null);
                                    } else {
                                        if (resultCustomerFromDb) {
                                            return this.reservationRest
                                                .replaceReservationCustomer(resRequestData?.prereservationId, resultCustomerFromDb.id)
                                                .pipe(
                                                    mergeMap(() => this.customerRest.deactivateCustomer(contractorId, customerNew.id)),
                                                    map(() => resultCustomerFromDb),
                                                );
                                        } else {
                                            const customerRequestCopy = _.cloneDeep(customerNew);
                                            customerRequestCopy.createdFrom = CusomerCreatedFromType.EAMBULANTA;
                                            const reservationUpdate: EventUpdate = new EventUpdate().deserialize({ customer: customerRequestCopy });
                                            return this.customerRest
                                                .updateCustomer(contractorId, customerRequestCopy.id, customerRequestCopy, true)
                                                .pipe(
                                                    tap(() => (customerNew.createdFrom = CusomerCreatedFromType.EAMBULANTA)),
                                                    mergeMap(() =>
                                                        this.reservationRest.updateReservation(resRequestData?.prereservationId, reservationUpdate),
                                                    ),
                                                    map(() => resultCustomerFromDb),
                                                );
                                        }
                                    }
                                }),
                            );
                        }),
                    );
                }
            }),
        );
    }

    private createCustomerFromExternalEvent(contractorId: number, customerNew: Customer): Observable<unknown> {
        const customerRequestCopy = _.cloneDeep(customerNew);
        customerRequestCopy.createdFrom = CusomerCreatedFromType.EAMBULANTA;
        return this.customerRest
            .updateCustomer(contractorId, customerRequestCopy.id, customerRequestCopy, true)
            .pipe(tap(() => (customerNew.createdFrom = CusomerCreatedFromType.EAMBULANTA)));
    }
}
